import type { ReactNode } from 'react';
import { cx } from './primitives/cx';

/**
 * P-076 (P6) · Las acciones de un detalle o de una fila, en el bloque donde ya estaban: legibles, con objetivo
 * táctil, y lo destructivo aparte (después de una línea en el teléfono, a la derecha en escritorio). No mueve
 * ninguna acción a otra superficie ni la esconde en un menú: eso cambiaría dónde y cuándo se actúa.
 *
 * `children` son las acciones de siempre (Copiar, Imprimir, Editar…); `danger`, la destructiva (Anular,
 * Eliminar), que en el rediseño es texto rojo (`Button variant="danger"`); `note`, un aviso corto («Ya
 * solicitaste un cambio…»). `stack` apila las acciones a todo el ancho en el teléfono.
 *
 * P-078 (ActionBar v2) · `primary` es LA acción de la pantalla (Aprobar y confirmar, Registrar movimiento,
 * Siguiente). El DOM es el orden visual en todos los anchos: nota · secundarias (`children`) · primaria | destructiva.
 * En el teléfono las secundarias van en dos columnas y la primaria a todo el ancho cerrando el grupo (lo más
 * cercano al pulgar); en escritorio todo el grupo va en un renglón a la izquierda y lo destructivo a la derecha.
 * Una nota (`note`) acepta texto o un `FooterReason`: va en su propio renglón, antes de los botones.
 */
export function ActionBar({ children, primary, danger, note, stack = false, className, 'aria-label': ariaLabel }: {
  children?: ReactNode;
  primary?: ReactNode;
  danger?: ReactNode;
  note?: ReactNode;
  stack?: boolean;
  className?: string;
  'aria-label'?: string;
}) {
  const hasPrimary = Boolean(primary);
  return (
    <div aria-label={ariaLabel} className={cx('n3-actions', stack && 'n3-actions--stack', hasPrimary && 'n3-actions--primary', className)} role={ariaLabel ? 'group' : undefined}>
      {note && <div className="n3-actions__note">{note}</div>}
      {(children || hasPrimary) && (
        <div className="n3-actions__group">
          {children}
          {hasPrimary && <div className="n3-actions__primary">{primary}</div>}
        </div>
      )}
      {danger && <div className="n3-actions__danger"><div className="n3-actions__group">{danger}</div></div>}
    </div>
  );
}
